import { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import { ToolLayout } from '../ToolLayout';
import { CodeEditor } from '../CodeEditor';

const SAMPLES: Record<Direction, string> = {
  'json-to-csv': '[\n  { "id": 1, "name": "Alice", "role": "Engineer", "active": true },\n  { "id": 2, "name": "Bob", "role": "Designer", "active": false },\n  { "id": 3, "name": "Carol", "role": "Manager", "active": true }\n]',
  'csv-to-json': 'id,name,role,active\n1,Alice,Engineer,true\n2,Bob,Designer,false\n3,Carol,Manager,true',
};

type Direction = 'json-to-csv' | 'csv-to-json';

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), height: 'calc(100vh - 260px)', minHeight: 400 },
  controls: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' },
  row: { flex: 1, display: 'flex', gap: theme.spacing(2), alignItems: 'stretch', overflow: 'hidden' },
  pane: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    minHeight: 36,
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
}));

// ── JSON → CSV ────────────────────────────────────────────────────────────────
function jsonToCsv(input: string): { result: string; error: null } | { result: null; error: string } {
  let parsed: unknown;
  try { parsed = JSON.parse(input); } catch (e: any) { return { result: null, error: `Invalid JSON: ${e?.message}` }; }

  const rows: Record<string, unknown>[] = Array.isArray(parsed) ? parsed as any[] : [parsed as any];

  if (!rows.every(r => r && typeof r === 'object' && !Array.isArray(r))) {
    return { result: null, error: 'JSON must be an array of objects (or a single object) to convert to CSV.' };
  }

  const headers = Array.from(new Set(rows.flatMap(r => Object.keys(r as object))));

  const escape = (val: unknown): string => {
    let s: string;
    if (val === null || val === undefined) s = '';
    else if (typeof val === 'object') s = JSON.stringify(val);
    else s = String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const lines = [
    headers.map(escape).join(','),
    ...rows.map(r => headers.map(h => escape((r as any)[h])).join(',')),
  ];

  return { result: lines.join('\n'), error: null };
}

// ── CSV → JSON ────────────────────────────────────────────────────────────────
function csvToJson(input: string): { result: string; error: null } | { result: null; error: string } {
  const lines = input.trim().split('\n');
  if (lines.length < 2) return { result: null, error: 'CSV must have at least a header row and one data row.' };

  const parseRow = (line: string): string[] => {
    const cells: string[] = [];
    let inQuote = false;
    let cell = '';
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { cell += '"'; i++; }
        else inQuote = !inQuote;
      } else if (ch === ',' && !inQuote) {
        cells.push(cell); cell = '';
      } else {
        cell += ch;
      }
    }
    cells.push(cell);
    return cells;
  };

  try {
    const headers = parseRow(lines[0]);
    const result = lines.slice(1).filter((l, i, arr) => {
      // Only drop blank lines at the very end of the file, not mid-data
      if (l.trim()) return true;
      return arr.slice(i + 1).some(r => r.trim());
    }).map(line => {
      const vals = parseRow(line);
      return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? '']));
    });
    return { result: JSON.stringify(result, null, 2), error: null };
  } catch (e: any) {
    return { result: null, error: `Failed to parse CSV: ${e?.message}` };
  }
}

export function JsonCsvPage() {
  const { classes } = useStyles();
  const [direction, setDirection] = useState<Direction>('json-to-csv');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const convert = useCallback((value: string, dir: Direction) => {
    if (!value.trim()) { setOutput(''); setError(null); return; }
    const { result, error: err } = dir === 'json-to-csv' ? jsonToCsv(value) : csvToJson(value);
    if (err) { setError(err); setOutput(''); }
    else { setOutput(result!); setError(null); }
  }, []);

  const handleInputChange = (value: string) => {
    setInput(value);
    convert(value, direction);
  };

  const handleDirectionChange = (_: unknown, val: Direction | null) => {
    if (!val) return;
    const newInput = output || input;
    setDirection(val);
    setInput(newInput);
    convert(newInput, val);
  };

  const clear = () => { setInput(''); setOutput(''); setError(null); setCopied(false); };

  const loadSample = () => {
    const sample = SAMPLES[direction];
    setInput(sample);
    convert(sample, direction);
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const ext = direction === 'json-to-csv' ? 'csv' : 'json';
    const mime = direction === 'json-to-csv' ? 'text/csv' : 'application/json';
    const blob = new Blob([output], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `output.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };

  const inputLabel  = direction === 'json-to-csv' ? 'JSON Input' : 'CSV Input';
  const outputLabel = direction === 'json-to-csv' ? 'CSV Output' : 'JSON Output';
  const inputPlaceholder = direction === 'json-to-csv'
    ? '[\n  { "name": "Alice", "role": "Engineer" },\n  { "name": "Bob", "role": "Designer" }\n]'
    : 'name,role\nAlice,Engineer\nBob,Designer';

  return (
    <ToolLayout
      title="JSON ↔ CSV"
      subtitle="Convert between JSON arrays and CSV — switch direction to swap"
    >
      <Box className={classes.layout}>
        <Box className={classes.controls}>
          <ToggleButtonGroup
            size="small"
            value={direction}
            exclusive
            onChange={handleDirectionChange}
          >
            <ToggleButton value="json-to-csv" sx={{ textTransform: 'none', fontSize: 13 }}>JSON → CSV</ToggleButton>
            <ToggleButton value="csv-to-json" sx={{ textTransform: 'none', fontSize: 13 }}>CSV → JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box className={classes.row}>
          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>{inputLabel}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Button size="small" variant="outlined" onClick={loadSample} sx={{ textTransform: 'none', fontSize: 12 }}>
                  Load sample
                </Button>
                <Tooltip title="Clear">
                  <span>
                    <IconButton size="small" onClick={clear} disabled={!input && !output}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={input}
              onChange={handleInputChange}
              sx={{ flex: 1 }}
              placeholder={inputPlaceholder}
            />
          </Box>

          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>{outputLabel}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
                  <span>
                    <IconButton size="small" onClick={copyOutput} disabled={!output}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <IconButton size="small" onClick={download} disabled={!output}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={output}
              readOnly
              sx={{ flex: 1 }}
              placeholder="Output will appear here…"
            />
          </Box>
        </Box>
      </Box>
    </ToolLayout>
  );
}
